/**
 * MarinaMap. Mapbox satellite tiles (Leaflet) + SVG overlay for slips
 *
 * Architecture:
 *   [Leaflet MapContainer]  ← satellite tiles, zoom/pan (Leaflet owns this)
 *         +
 *   [SVG createPortal]      ← slip polygons, labels, edit handles
 *
 * Event handling:
 *   - Slip mousedown calls e.stopPropagation() → Leaflet never sees it
 *   - Drag uses document.addEventListener(mousemove/mouseup). works anywhere
 *   - map.dragging.disable/enable called synchronously in the event handler
 *   - Zero conflicts between SVG and Leaflet
 */

import React, {
  useState, useEffect, useCallback, useRef, useMemo, useReducer,
} from "react";
import { createPortal } from "react-dom";
import {
  Box, Typography, Paper, Button, Chip, Select, MenuItem, FormControl,
  InputLabel, Tooltip as MuiTooltip, IconButton, CircularProgress,
  Snackbar, Alert, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, Divider, Table, TableBody, TableRow, TableCell, TableHead,
} from "@mui/material";
import EventIcon        from "@mui/icons-material/Event";
import AddTaskIcon      from "@mui/icons-material/AddTask";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import ZoomInIcon          from "@mui/icons-material/ZoomIn";
import ZoomOutIcon         from "@mui/icons-material/ZoomOut";
import FitScreenIcon       from "@mui/icons-material/FitScreen";
import EditIcon            from "@mui/icons-material/Edit";
import SaveIcon            from "@mui/icons-material/Save";
import MyLocationIcon      from "@mui/icons-material/MyLocation";
import PlaceIcon           from "@mui/icons-material/Place";
import DirectionsBoatIcon  from "@mui/icons-material/DirectionsBoat";
import CloseIcon           from "@mui/icons-material/Close";
import api from "../../utils/api";

// ── Constants ─────────────────────────────────────────────────────────────────
const DEFAULT_ZOOM   = 19;
const M_PER_LAT      = 111139;
const FT_PER_M       = 3.28084;

const STATUS_STYLE = {
  available:   { fill:"#22c55e", stroke:"#15803d", opacity:0.60 },
  reserved:    { fill:"#60a5fa", stroke:"#1e4c8a", opacity:0.65 },
  occupied:    { fill:"#fb923c", stroke:"#b45309", opacity:0.65 },
  maintenance: { fill:"#9ca3af", stroke:"#4b5563", opacity:0.50 },
};
const STATUS_LABELS = {
  available:"Available", reserved:"Reserved",
  occupied:"Occupied",   maintenance:"Maintenance",
};

// ── GPS helpers ───────────────────────────────────────────────────────────────
const mPerLng = lat => M_PER_LAT * Math.cos(lat * Math.PI / 180);

/** Slip → 4 GPS [lat,lng] corners. */
function slipGpsCorners(slip) {
  const { lat_center: lat, lng_center: lng } = slip;
  if (lat == null || lng == null) return null;
  const wM  = (slip.max_beam   || 15) / FT_PER_M;
  const hM  = (slip.max_length || 50) / FT_PER_M;
  const mLng = mPerLng(lat);
  const hw   = (wM / 2) / mLng;
  const hh   = (hM / 2) / M_PER_LAT;
  const rot  = (slip.map_rotation || 0) * Math.PI / 180;
  const cos  = Math.cos(rot), sin = Math.sin(rot);
  return [[-hw,-hh],[hw,-hh],[hw,hh],[-hw,hh]].map(([dx,dy]) => {
    const mx = dx * mLng, my = dy * M_PER_LAT;
    return [lat + (mx*sin + my*cos)/M_PER_LAT, lng + (mx*cos - my*sin)/mLng];
  });
}

/** Rotation handle GPS position. offset along slip heading. */
function rotHandleGps(slip) {
  const { lat_center: lat, lng_center: lng } = slip;
  if (lat == null) return null;
  const hM   = ((slip.max_length || 50) / FT_PER_M) / 2 + 6; // 6m past tip
  const rot  = (slip.map_rotation || 0) * Math.PI / 180;
  const mLng = mPerLng(lat);
  return [lat + (hM * Math.cos(rot)) / M_PER_LAT, lng + (hM * Math.sin(rot)) / mLng];
}

// ═════════════════════════════════════════════════════════════════════════════
// SVG OVERLAY. rendered via createPortal inside Leaflet container
// ═════════════════════════════════════════════════════════════════════════════
function SlipSVGLayer({
  slips, editMode, selectedId, onSelect,
  hiddenStatuses, onSlipUpdate, onMarkDirty, onPlaced,
  placingId, slipFitsVessel, fitActive,
}) {
  const map = useMap();
  const [, rerender] = useReducer(x => x + 1, 0); // trigger on map move

  // Re-render whenever the map view changes so pixels stay in sync
  useEffect(() => {
    const tick = () => rerender();
    map.on("move zoom resize", tick);
    return () => map.off("move zoom resize", tick);
  }, [map]);

  // GPS → container pixels
  const px = useCallback((lat, lng) => {
    const p = map.latLngToContainerPoint(L.latLng(lat, lng));
    return [p.x, p.y];
  }, [map]); // map.latLngToContainerPoint always uses current view

  // Container pixels → GPS
  const gps = useCallback((x, y) => {
    const ll = map.containerPointToLatLng(L.point(x, y));
    return { lat: ll.lat, lng: ll.lng };
  }, [map]);

  // ── Drag / rotate / resize via document listeners ─────────────────────────
  const startInteraction = useCallback((mode, slipId, startEvent) => {
    startEvent.stopPropagation();
    startEvent.preventDefault();
    map.dragging.disable();

    const rect = map.getContainer().getBoundingClientRect();
    let lastX  = startEvent.clientX;
    let lastY  = startEvent.clientY;

    const onMove = (e) => {
      // Current and previous positions as GPS
      const cur  = gps(e.clientX - rect.left,  e.clientY - rect.top);
      const prev = gps(lastX - rect.left,       lastY - rect.top);
      const dLat = cur.lat - prev.lat;
      const dLng = cur.lng - prev.lng;

      onSlipUpdate(slipId, (slip) => {
        if (mode === "move") {
          return { lat_center: slip.lat_center + dLat, lng_center: slip.lng_center + dLng };
        }

        if (mode === "rotate") {
          const [cpx, cpy] = [
            e.clientX - rect.left - map.latLngToContainerPoint(L.latLng(slip.lat_center, slip.lng_center)).x,
            e.clientY - rect.top  - map.latLngToContainerPoint(L.latLng(slip.lat_center, slip.lng_center)).y,
          ];
          const angle = Math.atan2(cpy, cpx) * 180 / Math.PI + 90;
          return { map_rotation: angle };
        }

        if (mode.startsWith("resize-")) {
          const cornerIdx = parseInt(mode.split("-")[1], 10);
          const mLng2 = mPerLng(slip.lat_center || 0);
          const rot   = (slip.map_rotation || 0) * Math.PI / 180;
          const cos   = Math.cos(rot), sin = Math.sin(rot);
          // Project GPS delta into slip's local axis (meters)
          const dxM = dLng * mLng2;
          const dyM = dLat * M_PER_LAT;
          const localX = dxM * cos + dyM * sin;   // across-slip (beam direction)
          const localY = -dxM * sin + dyM * cos;  // along-slip  (length direction)
          const signs  = [[-1,-1],[1,-1],[1,1],[-1,1]][cornerIdx];
          return {
            max_beam:   Math.max(3,  parseFloat(((slip.max_beam   || 15) + signs[0] * localX * FT_PER_M * 2).toFixed(1))),
            max_length: Math.max(10, parseFloat(((slip.max_length || 50) + signs[1] * localY * FT_PER_M * 2).toFixed(1))),
          };
        }
        return {};
      });

      onMarkDirty(slipId);
      lastX = e.clientX;
      lastY = e.clientY;
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup",   onUp);
      map.dragging.enable();
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup",   onUp);
  }, [map, gps, onSlipUpdate, onMarkDirty]);

  // Click on map to place an unplaced slip
  useEffect(() => {
    if (!placingId) return;
    const onClick = (e) => {
      const { lat, lng } = e.latlng;
      onPlaced(placingId, lat, lng);
    };
    map.once("click", onClick);
    return () => map.off("click", onClick);
  }, [placingId, map, onPlaced]);

  // ── Build SVG content ─────────────────────────────────────────────────────
  const svgEl = (
    <svg
      style={{
        position: "absolute", inset: 0, width: "100%", height: "100%",
        zIndex: 450, overflow: "visible", pointerEvents: "none",
      }}
    >
      {slips
        .filter(s => s.lat_center != null && !hiddenStatuses.has(s.status))
        .map(slip => {
          const gpsCrn  = slipGpsCorners(slip);
          if (!gpsCrn) return null;
          const pxCrn   = gpsCrn.map(([lat, lng]) => px(lat, lng));
          const pts      = pxCrn.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
          const [cx, cy] = px(slip.lat_center, slip.lng_center);
          const st       = STATUS_STYLE[slip.status] || STATUS_STYLE.available;
          const isSel    = selectedId === slip.id;
          const rotHGps  = (editMode && isSel) ? rotHandleGps(slip) : null;
          const rotHPx   = rotHGps ? px(rotHGps[0], rotHGps[1]) : null;
          const fits     = slipFitsVessel ? slipFitsVessel(slip) : true;
          const dimmed   = fitActive && !fits;

          return (
            <g key={slip.id} style={{ pointerEvents: "all", opacity: dimmed ? 0.18 : 1, transition:"opacity 0.25s" }}>
              {/* Vessel-fit highlight ring */}
              {fitActive && fits && (
                <polygon points={pts} fill="none" stroke="#7c3aed" strokeWidth={4} strokeDasharray="6 3" opacity={0.9} style={{ pointerEvents:"none" }} />
              )}
              {/* Slip body */}
              <polygon
                points={pts}
                fill={fits && fitActive ? "#7c3aed" : st.fill}
                fillOpacity={isSel ? Math.min(st.opacity + 0.2, 1) : st.opacity}
                stroke={isSel ? "#fbbf24" : (fits && fitActive ? "#5b21b6" : st.stroke)}
                strokeWidth={isSel ? 3 : 2}
                style={{ cursor: editMode ? "grab" : "pointer" }}
                onMouseDown={e => {
                  e.stopPropagation();
                  onSelect(slip.id);
                  if (editMode) startInteraction("move", slip.id, e);
                }}
              />

              {/* Slip number */}
              <text
                x={cx.toFixed(1)} y={cy.toFixed(1)}
                textAnchor="middle" dominantBaseline="middle"
                fontSize="10" fontWeight="700" fill="white"
                style={{ pointerEvents: "none", userSelect: "none",
                  filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.9))" }}
              >
                {slip.slip_number}
              </text>

              {/* Edit handles. only when selected in edit mode */}
              {editMode && isSel && (
                <>
                  {/* Corner resize handles */}
                  {pxCrn.map(([hx, hy], i) => (
                    <circle key={i}
                      cx={hx.toFixed(1)} cy={hy.toFixed(1)} r="6"
                      fill="#2f63ad" stroke="white" strokeWidth="2"
                      style={{ cursor: "nwse-resize" }}
                      onMouseDown={e => { e.stopPropagation(); startInteraction(`resize-${i}`, slip.id, e); }}
                    />
                  ))}

                  {/* Rotation dashed line */}
                  {rotHPx && (
                    <line
                      x1={cx.toFixed(1)} y1={cy.toFixed(1)}
                      x2={rotHPx[0].toFixed(1)} y2={rotHPx[1].toFixed(1)}
                      stroke="#fbbf24" strokeWidth="1.5" strokeDasharray="4 3"
                      style={{ pointerEvents: "none" }}
                    />
                  )}

                  {/* Rotation handle */}
                  {rotHPx && (
                    <circle
                      cx={rotHPx[0].toFixed(1)} cy={rotHPx[1].toFixed(1)} r="8"
                      fill="#f59e0b" stroke="white" strokeWidth="2"
                      style={{ cursor: "alias" }}
                      onMouseDown={e => { e.stopPropagation(); startInteraction("rotate", slip.id, e); }}
                    />
                  )}
                </>
              )}
            </g>
          );
        })}
    </svg>
  );

  return createPortal(svgEl, map.getContainer());
}

// ── Fit controller ────────────────────────────────────────────────────────────
function MapController({ onReady, fitBounds, triggerFit }) {
  const map = useMap();
  useEffect(() => { onReady(map); }, [map, onReady]);
  useEffect(() => {
    if (fitBounds && triggerFit > 0) {
      try { map.fitBounds(fitBounds, { animate: true, padding: [30, 30] }); } catch {}
    }
  }, [triggerFit, fitBounds, map]);
  return null;
}

// ── Set location dialog ───────────────────────────────────────────────────────
function SetLocationDialog({ open, onClose, marina, onSaved }) {
  const [lat, setLat] = useState(""); const [lng, setLng] = useState("");
  const [saving, setSaving] = useState(false); const [err, setErr] = useState("");
  useEffect(() => { if (open) { setLat(marina?.latitude ?? ""); setLng(marina?.longitude ?? ""); setErr(""); } }, [open, marina]);
  const save = async () => {
    const la = parseFloat(lat), lo = parseFloat(lng);
    if (isNaN(la)||isNaN(lo)||la<-90||la>90||lo<-180||lo>180) { setErr("Enter valid coordinates"); return; }
    setSaving(true);
    try { await api.put(`/marinas/${marina.id}`, { ...marina, latitude:la, longitude:lo }); onSaved(la, lo); onClose(); }
    catch { setErr("Failed to save location"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx:{ borderRadius:"16px" } }}>
      <DialogTitle sx={{ fontWeight:700 }}>Set Marina Location</DialogTitle>
      <DialogContent sx={{ display:"flex", flexDirection:"column", gap:2, pt:"12px !important" }}>
        <Alert severity="info" sx={{ borderRadius:"10px", fontSize:"0.78rem" }}>
          Google Maps → right-click your marina → copy lat, lng.
        </Alert>
        <TextField size="small" label="Latitude"  value={lat} onChange={e=>setLat(e.target.value)}  placeholder="28.3728"  sx={{ "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
        <TextField size="small" label="Longitude" value={lng} onChange={e=>setLng(e.target.value)} placeholder="-81.5158" sx={{ "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
        {err && <Alert severity="error" sx={{ borderRadius:"10px" }}>{err}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px:3, pb:2.5 }}>
        <Button onClick={onClose} sx={{ textTransform:"none", color:"#6b778c" }}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}
          sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, background:"#2f63ad" }}>
          {saving?"Saving…":"Save Location"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
const MarinaMap = ({ marinaId }) => {
  const [slips,       setSlips]       = useState([]);
  const [docks,       setDocks]       = useState([]);
  const [marina,      setMarina]      = useState(null);
  const [mapboxToken, setMapboxToken] = useState("");
  const [loading,     setLoading]     = useState(true);
  const [editMode,    setEditMode]    = useState(false);
  const [selectedId,  setSelectedId]  = useState(null);
  const [placingId,   setPlacingId]   = useState(null);
  const [dockFilter,  setDockFilter]  = useState("all");
  const [hiddenSt,    setHiddenSt]    = useState(new Set());
  const [dirty,       setDirty]       = useState(new Set());
  const [saving,      setSaving]      = useState(false);
  const [snack,       setSnack]       = useState({ open:false, msg:"", sev:"success" });
  const [fitTrigger,  setFitTrigger]  = useState(0);
  const [locDialog,   setLocDialog]   = useState(false);

  // ── Vessel fit filter ────────────────────────────────────────────────────────
  const [vessels,         setVessels]         = useState([]);
  const [vesselFitOpen,   setVesselFitOpen]   = useState(false);
  const [selectedVessel,  setSelectedVessel]  = useState(null); // vessel object
  const [manualLoa,       setManualLoa]       = useState("");
  const [manualBeam,      setManualBeam]      = useState("");
  const [manualDraft,     setManualDraft]     = useState("");
  const [fitDims,         setFitDims]         = useState(null); // { loa, beam, draft } | null

  // ── Reservation state ────────────────────────────────────────────────────────
  const [reserveOpen,      setReserveOpen]      = useState(false);
  const [slipReservations, setSlipReservations] = useState([]);
  const [loadingResv,      setLoadingResv]      = useState(false);
  const [reserveForm,      setReserveForm]      = useState({ vessel_id:"", start_date:"", end_date:"", reservation_type:"transient", notes:"" });
  const [savingResv,       setSavingResv]       = useState(false);
  const [resvError,        setResvError]        = useState("");
  const mapRef = useRef(null);

  const showSnack = (msg, sev="success") => setSnack({ open:true, msg, sev });
  const markDirty = useCallback(id => setDirty(prev => new Set(prev).add(id)), []);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!marinaId) return;
    setLoading(true);
    Promise.all([
      api.get(`/marina-slips?marina_id=${marinaId}`),
      api.get(`/marina-docks?marina_id=${marinaId}`),
      api.get(`/marinas/${marinaId}`).catch(() => null),
      api.get("/app-settings").catch(() => null),
      api.get(`/vessels`).catch(() => null),
    ]).then(([sr, dr, mr, cfg, vr]) => {
      setSlips(sr.data || []);
      setDocks(dr.data || []);
      if (mr?.data)                setMarina(mr.data);
      if (cfg?.data?.mapbox_token) setMapboxToken(cfg.data.mapbox_token);
      if (vr?.data)                setVessels(vr.data || []);
    }).catch(() => showSnack("Failed to load map", "error"))
      .finally(() => setLoading(false));
  }, [marinaId]);

  // ── Slip update (used by SVG layer during drag) ───────────────────────────
  const handleSlipUpdate = useCallback((slipId, updater) => {
    setSlips(prev => prev.map(s => s.id !== slipId ? s : { ...s, ...updater(s) }));
  }, []);

  // ── Place via map click ────────────────────────────────────────────────────
  const handlePlaced = useCallback((slipId, lat, lng) => {
    setSlips(prev => prev.map(s => s.id !== slipId ? s : { ...s, lat_center: lat, lng_center: lng }));
    markDirty(slipId);
    setPlacingId(null);
    showSnack("Slip placed. Save Layout to confirm");
  }, [markDirty]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    const toSave = slips.filter(s => dirty.has(s.id));
    if (!toSave.length) { showSnack("No changes to save"); return; }
    setSaving(true);
    try {
      await api.put(`/marina-map/slips/bulk`, {
        marina_id: marinaId,
        slips: toSave.map(s => ({
          id:s.id, lat_center:s.lat_center, lng_center:s.lng_center,
          map_rotation:s.map_rotation, map_width:s.map_width, map_height:s.map_height,
          max_beam:s.max_beam, max_length:s.max_length,
        })),
      });
      setDirty(new Set()); showSnack("Layout saved");
    } catch { showSnack("Failed to save", "error"); }
    finally { setSaving(false); }
  }, [slips, dirty, marinaId]);

  // ── Computed ──────────────────────────────────────────────────────────────
  const filteredSlips = useMemo(() =>
    slips.filter(s => dockFilter==="all" || String(s.dock_id)===dockFilter),
    [slips, dockFilter]);

  // Returns true if the slip can fit the current fitDims
  const slipFitsVessel = useCallback((slip) => {
    if (!fitDims) return true; // no filter active
    const { loa, beam, draft } = fitDims;
    if (loa  && slip.max_length && parseFloat(loa)  > parseFloat(slip.max_length)) return false;
    if (beam && slip.max_beam   && parseFloat(beam) > parseFloat(slip.max_beam))   return false;
    if (draft && slip.max_draft && parseFloat(draft) > parseFloat(slip.max_draft)) return false;
    if (slip.status !== "available") return false; // only show available slips
    return true;
  }, [fitDims]);

  const applyVesselFit = () => {
    const loa   = selectedVessel ? selectedVessel.loa   : parseFloat(manualLoa)   || null;
    const beam  = selectedVessel ? selectedVessel.beam  : parseFloat(manualBeam)  || null;
    const draft = selectedVessel ? selectedVessel.draft : parseFloat(manualDraft) || null;
    setFitDims({ loa, beam, draft });
    setVesselFitOpen(false);
    // Force show available, hide others
    setHiddenSt(new Set(["reserved","occupied","maintenance"]));
  };

  // Load reservations when a slip is selected
  const loadSlipReservations = useCallback(async (slipId) => {
    if (!slipId) return;
    setLoadingResv(true);
    try {
      const res = await api.get(`/slip-reservations?slip_id=${slipId}`);
      setSlipReservations(res.data || []);
    } catch { setSlipReservations([]); }
    finally { setLoadingResv(false); }
  }, []);

  const handleReserve = async () => {
    if (!reserveForm.vessel_id || !reserveForm.start_date || !reserveForm.end_date) {
      setResvError("Vessel, start date, and end date are required."); return;
    }
    setSavingResv(true); setResvError("");
    try {
      await api.post("/slip-reservations", {
        slip_id: selectedId,
        vessel_id: parseInt(reserveForm.vessel_id, 10),
        start_date: reserveForm.start_date,
        end_date:   reserveForm.end_date,
        reservation_type: reserveForm.reservation_type,
        notes: reserveForm.notes || null,
      });
      setReserveOpen(false);
      setReserveForm({ vessel_id:"", start_date:"", end_date:"", reservation_type:"transient", notes:"" });
      showSnack("Reservation created successfully");
      const sr = await api.get(`/marina-slips?marina_id=${marinaId}`);
      setSlips(sr.data || []);
      loadSlipReservations(selectedId);
    } catch (err) {
      setResvError(err.response?.data?.message || "Failed to create reservation.");
    } finally { setSavingResv(false); }
  };

  const clearVesselFit = () => {
    setFitDims(null);
    setSelectedVessel(null);
    setManualLoa(""); setManualBeam(""); setManualDraft("");
    setHiddenSt(new Set());
  };
  const unplacedSlips = useMemo(() => slips.filter(s => s.lat_center==null), [slips]);
  const marinaCenter  = marina?.latitude && marina?.longitude ? [marina.latitude, marina.longitude] : null;

  // Load reservations whenever the selected slip changes
  useEffect(() => {
    if (selectedId) {
      setSlipReservations([]);
      loadSlipReservations(selectedId);
      setReserveForm(f => ({ ...f, vessel_id:"", start_date:"", end_date:"" }));
    }
  }, [selectedId, loadSlipReservations]);

  const slipBounds = useMemo(() => {
    const pts = [];
    filteredSlips.filter(s=>s.lat_center!=null).forEach(s => {
      const c = slipGpsCorners(s); if(c) c.forEach(p=>pts.push(p));
    });
    if (!pts.length && marinaCenter) return [marinaCenter, marinaCenter];
    return pts.length > 1 ? pts : null;
  }, [filteredSlips, marinaCenter]);

  const tileUrl = mapboxToken
    ? `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/256/{z}/{x}/{y}?access_token=${mapboxToken}`
    : "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

  const selectedSlip = slips.find(s => s.id === selectedId);

  if (loading) return <Box sx={{ display:"flex", justifyContent:"center", py:10 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ display:"flex", flexDirection:"column", height:"100%", gap:0 }}>

      {/* Toolbar */}
      <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:1.5, flexWrap:"wrap" }}>
        <FormControl size="small" sx={{ minWidth:140 }}>
          <InputLabel>Dock</InputLabel>
          <Select value={dockFilter} label="Dock" onChange={e=>setDockFilter(e.target.value)} sx={{ borderRadius:"10px" }}>
            <MenuItem value="all">All Docks</MenuItem>
            {docks.map(d=><MenuItem key={d.id} value={String(d.id)}>{d.name}</MenuItem>)}
          </Select>
        </FormControl>

        <Box sx={{ display:"flex", gap:0.75, flexWrap:"wrap" }}>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <Chip key={key} label={label} size="small" onClick={() => setHiddenSt(prev => { const n=new Set(prev); n.has(key)?n.delete(key):n.add(key); return n; })}
              sx={{ height:26, fontWeight:600, fontSize:"0.72rem", cursor:"pointer",
                backgroundColor:hiddenSt.has(key)?"rgba(0,0,0,0.06)":`${STATUS_STYLE[key].fill}33`,
                color:hiddenSt.has(key)?"#9aa5b4":STATUS_STYLE[key].stroke,
                border:`1px solid ${hiddenSt.has(key)?"rgba(0,0,0,0.08)":STATUS_STYLE[key].stroke}55`,
                textDecoration:hiddenSt.has(key)?"line-through":"none" }} />
          ))}
        </Box>

        {/* Vessel fit active badge */}
        {fitDims && (
          <Chip
            icon={<DirectionsBoatIcon sx={{ fontSize:15 }} />}
            label={`Fitting: ${fitDims.loa ? `${fitDims.loa}ft LOA` : ""}${fitDims.beam ? ` × ${fitDims.beam}ft beam` : ""}`}
            onDelete={clearVesselFit}
            size="small"
            sx={{ fontWeight:700, backgroundColor:"rgba(124,58,237,0.12)", color:"#7c3aed", border:"1px solid rgba(124,58,237,0.3)", borderRadius:"8px" }}
          />
        )}

        <Box sx={{ flex:1 }} />

        {/* Find slips for vessel */}
        <MuiTooltip title="Find slips that fit a vessel">
          <Button size="small" variant={fitDims?"contained":"outlined"} startIcon={<DirectionsBoatIcon sx={{ fontSize:15 }} />}
            onClick={() => setVesselFitOpen(true)}
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, fontSize:"0.78rem",
              ...(fitDims ? { background:"#7c3aed","&:hover":{background:"#6d28d9"} } : { borderColor:"#7c3aed", color:"#7c3aed" }) }}>
            {fitDims ? "Change Vessel" : "Find Slip for Vessel"}
          </Button>
        </MuiTooltip>

        <Box sx={{ display:"flex", alignItems:"center", gap:0.75 }}>
          <MuiTooltip title="Zoom in"><IconButton size="small" onClick={()=>mapRef.current?.zoomIn()}><ZoomInIcon /></IconButton></MuiTooltip>
          <MuiTooltip title="Zoom out"><IconButton size="small" onClick={()=>mapRef.current?.zoomOut()}><ZoomOutIcon /></IconButton></MuiTooltip>
          <MuiTooltip title="Fit all slips"><IconButton size="small" onClick={()=>setFitTrigger(t=>t+1)}><FitScreenIcon /></IconButton></MuiTooltip>
          <MuiTooltip title="Set marina GPS location"><IconButton size="small" onClick={()=>setLocDialog(true)}><MyLocationIcon /></IconButton></MuiTooltip>

          <Button variant={editMode?"contained":"outlined"} size="small" startIcon={<EditIcon sx={{fontSize:15}}/>}
            onClick={()=>{ setEditMode(e=>!e); setSelectedId(null); setPlacingId(null); }}
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, ...(editMode?{background:"#2f63ad"}:{borderColor:"#2f63ad",color:"#2f63ad"}) }}>
            {editMode ? "Editing" : "Edit Layout"}
          </Button>

          {editMode && (
            <Button variant="contained" size="small"
              startIcon={saving?<CircularProgress size={13} color="inherit"/>:<SaveIcon sx={{fontSize:15}}/>}
              disabled={saving||!dirty.size} onClick={handleSave}
              sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, background:"#16a34a", "&:hover":{background:"#15803d"} }}>
              Save{dirty.size?` (${dirty.size})`:""}
            </Button>
          )}
        </Box>
      </Box>

      {/* Map + Sidebar */}
      <Box sx={{ display:"flex", gap:2, flex:1, minHeight:0 }}>

        {/* Map */}
        <Box sx={{ flex:1, borderRadius:"16px", overflow:"hidden", border:"1px solid rgba(47,99,173,0.10)", boxShadow:"0 4px 24px rgba(0,0,0,0.12)", minHeight:400, position:"relative" }}>
          {!marinaCenter ? (
            <Box sx={{ height:"100%", minHeight:400, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", backgroundColor:"#1a2535", gap:2 }}>
              <PlaceIcon sx={{ fontSize:48, color:"rgba(255,255,255,0.3)" }} />
              <Typography variant="h6" sx={{ color:"rgba(255,255,255,0.7)", fontWeight:700 }}>Marina location not set</Typography>
              <Typography variant="body2" sx={{ color:"rgba(255,255,255,0.4)", mb:1 }}>Set GPS coordinates to show the satellite view</Typography>
              <Button variant="contained" startIcon={<MyLocationIcon />} onClick={()=>setLocDialog(true)}
                sx={{ borderRadius:"12px", textTransform:"none", fontWeight:700, background:"#2f63ad" }}>
                Set Marina Location
              </Button>
            </Box>
          ) : (
            <MapContainer center={marinaCenter} zoom={DEFAULT_ZOOM} maxZoom={22}
              style={{ height:"100%", width:"100%", background:"#1a2535" }}
              scrollWheelZoom attributionControl={false} zoomControl={false}>

              <MapController onReady={m=>{ mapRef.current=m; }} fitBounds={slipBounds} triggerFit={fitTrigger} />
              <TileLayer url={tileUrl} maxZoom={22} maxNativeZoom={22} />

              {/* SVG slip overlay. portaled into the Leaflet container */}
              <SlipSVGLayer
                slips={filteredSlips}
                editMode={editMode}
                selectedId={selectedId}
                onSelect={setSelectedId}
                hiddenStatuses={hiddenSt}
                onSlipUpdate={handleSlipUpdate}
                onMarkDirty={markDirty}
                onPlaced={handlePlaced}
                placingId={placingId}
                slipFitsVessel={slipFitsVessel}
                fitActive={!!fitDims}
              />
            </MapContainer>
          )}

          {/* Status bar */}
          {marinaCenter && (
            <Box sx={{ position:"absolute", bottom:8, left:8, zIndex:1000, display:"flex", gap:1, pointerEvents:"none" }}>
              <Typography variant="caption" sx={{ backgroundColor:"rgba(0,0,0,0.55)", color:"#fff", px:1, py:0.25, borderRadius:"6px", fontSize:"0.63rem" }}>
                {mapboxToken?"Mapbox Satellite":"Esri World Imagery"}
              </Typography>
              {dirty.size>0 && <Typography variant="caption" sx={{ backgroundColor:"rgba(234,88,12,0.85)", color:"#fff", px:1, py:0.25, borderRadius:"6px", fontSize:"0.63rem" }}>{dirty.size} unsaved</Typography>}
            </Box>
          )}

          {editMode && marinaCenter && !placingId && (
            <Box sx={{ position:"absolute", bottom:8, right:8, zIndex:1000, pointerEvents:"none" }}>
              <Typography variant="caption" sx={{ backgroundColor:"rgba(0,0,0,0.60)", color:"#fff", px:1.5, py:0.5, borderRadius:"8px", fontSize:"0.65rem" }}>
                🔵 Drag slip · ⬜ Corners = resize · 🟡 Handle = rotate
              </Typography>
            </Box>
          )}

          {placingId && marinaCenter && (
            <Box sx={{ position:"absolute", top:8, left:"50%", transform:"translateX(-50%)", zIndex:1000, pointerEvents:"none" }}>
              <Typography variant="caption" sx={{ backgroundColor:"rgba(47,99,173,0.9)", color:"#fff", px:2, py:0.75, borderRadius:"10px", fontWeight:600, fontSize:"0.75rem" }}>
                📍 Click satellite map to place slip {slips.find(s=>s.id===placingId)?.slip_number}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Sidebar */}
        <Paper elevation={0} sx={{ width:230, borderRadius:"14px", border:"1px solid rgba(47,99,173,0.10)", p:2, display:"flex", flexDirection:"column", gap:2, flexShrink:0, overflow:"auto" }}>

          <Box sx={{ display:"flex", flexDirection:"column", gap:0.75 }}>
            <Typography variant="caption" sx={{ fontWeight:700, color:"#6b778c", textTransform:"uppercase", letterSpacing:0.8, fontSize:"0.62rem" }}>Slip Status</Typography>
            {Object.entries(STATUS_LABELS).map(([key, label]) => {
              const count = slips.filter(s=>s.status===key).length;
              if (!count) return null;
              return (
                <Box key={key} sx={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <Typography variant="caption" sx={{ color:"#6b778c" }}>{label}</Typography>
                  <Chip label={count} size="small" sx={{ height:18, fontSize:"0.63rem", fontWeight:700, backgroundColor:`${STATUS_STYLE[key].fill}30`, color:STATUS_STYLE[key].stroke }} />
                </Box>
              );
            })}
          </Box>

          {/* Vessel fit summary */}
          {fitDims && (
            <Box sx={{ pt:1, borderTop:"1px solid rgba(124,58,237,0.15)", display:"flex", flexDirection:"column", gap:0.75 }}>
              <Typography variant="caption" sx={{ fontWeight:700, color:"#7c3aed", textTransform:"uppercase", letterSpacing:0.8, fontSize:"0.62rem" }}>
                <DirectionsBoatIcon sx={{ fontSize:11, mr:0.5, verticalAlign:"middle" }} />Vessel Fit Filter
              </Typography>
              <Typography variant="caption" sx={{ color:"#6b778c" }}>
                {filteredSlips.filter(s => slipFitsVessel(s)).length} slips fit
              </Typography>
              {fitDims.loa   && <Typography variant="caption" sx={{ color:"#7c3aed" }}>LOA: ≤{fitDims.loa}ft</Typography>}
              {fitDims.beam  && <Typography variant="caption" sx={{ color:"#7c3aed" }}>Beam: ≤{fitDims.beam}ft</Typography>}
              {fitDims.draft && <Typography variant="caption" sx={{ color:"#7c3aed" }}>Draft: ≤{fitDims.draft}ft</Typography>}
              <Button size="small" onClick={clearVesselFit}
                sx={{ borderRadius:"8px", textTransform:"none", fontWeight:600, fontSize:"0.65rem", color:"#dc2626", border:"1px solid rgba(220,38,38,0.3)", mt:0.5 }}>
                Clear Filter
              </Button>
            </Box>
          )}

          {/* Unplaced slips */}
          {unplacedSlips.length > 0 && (
            <Box sx={{ pt:1, borderTop:"1px solid rgba(47,99,173,0.08)" }}>
              <Typography variant="caption" sx={{ fontWeight:700, color:editMode?"#d97706":"#9aa5b4", display:"block", mb:0.75, textTransform:"uppercase", letterSpacing:0.8, fontSize:"0.62rem" }}>
                Not on map ({unplacedSlips.length})
              </Typography>
              <Box sx={{ display:"flex", flexDirection:"column", gap:0.5 }}>
                {unplacedSlips.map(s => (
                  <Box key={s.id} sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", p:0.75, borderRadius:"8px", backgroundColor:"rgba(217,119,6,0.06)", border:"1px solid rgba(217,119,6,0.15)" }}>
                    <Typography variant="caption" sx={{ fontWeight:600, color:"#374151" }}>{s.slip_number}</Typography>
                    {editMode && placingId!==s.id && (
                      <Button size="small" onClick={()=>setPlacingId(s.id)}
                        sx={{ minWidth:0, px:1, py:0.2, fontSize:"0.62rem", fontWeight:700, borderRadius:"6px", color:"#d97706", border:"1px solid rgba(217,119,6,0.4)", textTransform:"none", lineHeight:1.4 }}>
                        Place
                      </Button>
                    )}
                    {placingId===s.id && <Chip label="Click map" size="small" sx={{ height:18, fontSize:"0.6rem", backgroundColor:"rgba(47,99,173,0.15)", color:"#2f63ad" }} />}
                  </Box>
                ))}
                {!editMode && <Typography variant="caption" sx={{ color:"#9aa5b4", fontSize:"0.65rem" }}>Enable Edit Layout to place slips</Typography>}
              </Box>
            </Box>
          )}

          {!unplacedSlips.length && <Typography variant="caption" sx={{ fontWeight:700, color:"#16a34a" }}>All slips placed ✓</Typography>}

          {/* Selected slip info + Reserve */}
          {selectedSlip && (
            <Box sx={{ pt:1, borderTop:"1px solid rgba(47,99,173,0.08)" }}>
              <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:0.75 }}>
                <Typography variant="caption" sx={{ fontWeight:700, color:"#223142" }}>
                  Slip {selectedSlip.slip_number}
                </Typography>
                <Chip size="small" label={STATUS_LABELS[selectedSlip.status]||selectedSlip.status}
                  sx={{ height:16, fontSize:"0.6rem", fontWeight:700,
                    backgroundColor:`${STATUS_STYLE[selectedSlip.status]?.fill||"#ccc"}30`,
                    color: STATUS_STYLE[selectedSlip.status]?.stroke||"#666" }} />
              </Box>

              {[
                ["Dock",   docks.find(d=>d.id===selectedSlip.dock_id)?.name||"N/A"],
                ["Width",  selectedSlip.max_beam   ? `${Number(selectedSlip.max_beam  ).toFixed(1)} ft`:"N/A"],
                ["Length", selectedSlip.max_length ? `${Number(selectedSlip.max_length).toFixed(1)} ft`:"N/A"],
                ["Rate",   selectedSlip.daily_rate  ? `$${selectedSlip.daily_rate}/day`:"N/A"],
              ].map(([k,v]) => (
                <Box key={k} sx={{ display:"flex", justifyContent:"space-between", mb:0.2 }}>
                  <Typography variant="caption" sx={{ color:"#9aa5b4", fontSize:"0.68rem" }}>{k}</Typography>
                  <Typography variant="caption" sx={{ fontWeight:600, color:"#374151", fontSize:"0.68rem" }}>{v}</Typography>
                </Box>
              ))}

              {/* Reserve button */}
              {selectedSlip.status !== "maintenance" && (
                <Button fullWidth size="small" variant="contained" startIcon={<AddTaskIcon sx={{fontSize:13}} />}
                  onClick={() => { setResvError(""); setReserveOpen(true); }}
                  sx={{ mt:1, borderRadius:"8px", textTransform:"none", fontWeight:700, fontSize:"0.72rem",
                    bgcolor:"#2f63ad", boxShadow:"none", "&:hover":{ bgcolor:"#1f4f94", boxShadow:"none" } }}>
                  Reserve This Slip
                </Button>
              )}

              {/* Upcoming reservations */}
              <Box sx={{ mt:1 }}>
                <Typography variant="caption" sx={{ fontWeight:700, color:"#6b778c", fontSize:"0.62rem", textTransform:"uppercase", letterSpacing:0.6 }}>
                  <EventIcon sx={{ fontSize:9, mr:0.5, verticalAlign:"middle" }} />Upcoming
                </Typography>
                {loadingResv ? (
                  <Box sx={{ display:"flex", justifyContent:"center", py:0.75 }}><CircularProgress size={14} /></Box>
                ) : slipReservations.filter(r => r.status !== "cancelled").length === 0 ? (
                  <Typography variant="caption" sx={{ color:"#16a34a", fontSize:"0.65rem", display:"block", mt:0.25 }}>
                    Available - no upcoming bookings
                  </Typography>
                ) : (
                  <Box sx={{ display:"flex", flexDirection:"column", gap:0.4, mt:0.5 }}>
                    {slipReservations.filter(r => r.status !== "cancelled").slice(0,4).map(r => (
                      <Box key={r.id} sx={{ p:0.6, borderRadius:"6px", bgcolor:"rgba(251,146,60,0.08)", border:"1px solid rgba(251,146,60,0.2)" }}>
                        <Typography variant="caption" sx={{ fontWeight:700, color:"#b45309", fontSize:"0.63rem", display:"block" }}>
                          {r.vessel_name || "Unknown vessel"}
                        </Typography>
                        <Typography variant="caption" sx={{ color:"#9aa5b4", fontSize:"0.6rem" }}>
                          {new Date(r.start_date).toLocaleDateString("en-US",{month:"short",day:"numeric"})} - {new Date(r.end_date).toLocaleDateString("en-US",{month:"short",day:"numeric"})}
                        </Typography>
                      </Box>
                    ))}
                    {slipReservations.filter(r=>r.status!=="cancelled").length > 4 && (
                      <Typography variant="caption" sx={{ color:"#9aa5b4", fontSize:"0.6rem" }}>
                        +{slipReservations.filter(r=>r.status!=="cancelled").length - 4} more...
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              <Button size="small" onClick={()=>setSelectedId(null)}
                sx={{ mt:0.75, textTransform:"none", color:"#9aa5b4", fontSize:"0.65rem", p:0 }}>
                Deselect
              </Button>
            </Box>
          )}
        </Paper>
      </Box>

      {/* ── Reserve Slip Dialog ──────────────────────────────────────────────── */}
      <Dialog open={reserveOpen} onClose={()=>setReserveOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx:{ borderRadius:"18px" } }}>
        <DialogTitle sx={{ fontWeight:800, color:"#223142", display:"flex", alignItems:"center", gap:1 }}>
          <AddTaskIcon sx={{ color:"#2f63ad" }} />
          Reserve Slip {selectedSlip?.slip_number}
          <Box sx={{ flex:1 }} />
          <IconButton size="small" onClick={()=>setReserveOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          {resvError && <Alert severity="error" sx={{ mb:2, borderRadius:2 }}>{resvError}</Alert>}

          {/* Slip summary */}
          <Box sx={{ p:1.5, mb:2, borderRadius:"10px", bgcolor:"rgba(47,99,173,0.04)", border:"1px solid rgba(47,99,173,0.1)",
            display:"flex", gap:3, flexWrap:"wrap" }}>
            {[
              ["Dock",   docks.find(d=>d.id===selectedSlip?.dock_id)?.name||"N/A"],
              ["Length", selectedSlip?.max_length ? `${Number(selectedSlip.max_length).toFixed(0)} ft` : "N/A"],
              ["Beam",   selectedSlip?.max_beam   ? `${Number(selectedSlip.max_beam  ).toFixed(0)} ft` : "N/A"],
              ["Rate",   selectedSlip?.daily_rate  ? `$${selectedSlip.daily_rate}/day` : "N/A"],
            ].map(([k,v]) => (
              <Box key={k}>
                <Typography variant="caption" sx={{ color:"#9ca3af", display:"block" }}>{k}</Typography>
                <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>{v}</Typography>
              </Box>
            ))}
          </Box>

          {/* Vessel selector */}
          <FormControl fullWidth sx={{ mb:2 }} size="small">
            <InputLabel>Vessel *</InputLabel>
            <Select value={reserveForm.vessel_id} label="Vessel *"
              onChange={e => setReserveForm(f => ({...f, vessel_id: e.target.value}))}
              sx={{ borderRadius:"10px" }}>
              <MenuItem value="">Select a vessel...</MenuItem>
              {vessels.map(v => (
                <MenuItem key={v.id} value={v.id}>
                  {v.vessel_name || "Unnamed"}{v.loa ? ` - ${v.loa}ft` : ""}
                  {v.owner_display ? ` (${v.owner_display})` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Dates */}
          <Box sx={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:2, mb:2 }}>
            <TextField label="Start Date *" type="date" size="small" InputLabelProps={{ shrink:true }}
              value={reserveForm.start_date}
              onChange={e => setReserveForm(f => ({...f, start_date: e.target.value}))}
              sx={{ "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
            <TextField label="End Date *" type="date" size="small" InputLabelProps={{ shrink:true }}
              value={reserveForm.end_date}
              onChange={e => setReserveForm(f => ({...f, end_date: e.target.value}))}
              sx={{ "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
          </Box>

          {/* Reservation type */}
          <FormControl fullWidth size="small" sx={{ mb:2 }}>
            <InputLabel>Reservation Type</InputLabel>
            <Select value={reserveForm.reservation_type} label="Reservation Type"
              onChange={e => setReserveForm(f => ({...f, reservation_type: e.target.value}))}
              sx={{ borderRadius:"10px" }}>
              <MenuItem value="transient">Transient (daily)</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="seasonal">Seasonal</MenuItem>
              <MenuItem value="annual">Annual</MenuItem>
            </Select>
          </FormControl>

          <TextField fullWidth multiline rows={2} size="small" label="Notes (optional)"
            value={reserveForm.notes}
            onChange={e => setReserveForm(f => ({...f, notes: e.target.value}))}
            sx={{ mb:2, "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />

          {/* Existing reservations for this slip */}
          {slipReservations.filter(r => r.status !== "cancelled").length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight:700, color:"#b45309", display:"block", mb:0.75 }}>
                Already booked dates for this slip:
              </Typography>
              <Box sx={{ display:"flex", flexDirection:"column", gap:0.5, maxHeight:120, overflowY:"auto" }}>
                {slipReservations.filter(r => r.status !== "cancelled").map(r => (
                  <Box key={r.id} sx={{ display:"flex", justifyContent:"space-between", p:0.75,
                    borderRadius:"8px", bgcolor:"rgba(251,146,60,0.06)", border:"1px solid rgba(251,146,60,0.2)" }}>
                    <Typography variant="caption" sx={{ fontWeight:700, color:"#b45309" }}>
                      {new Date(r.start_date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                      {" - "}
                      {new Date(r.end_date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                    </Typography>
                    <Chip size="small" label={r.vessel_name||"Unknown"} sx={{ height:18, fontSize:"0.6rem" }} />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px:3, pb:3, gap:1 }}>
          <Button onClick={()=>setReserveOpen(false)} variant="outlined"
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Cancel</Button>
          <Button onClick={handleReserve} variant="contained" disabled={savingResv}
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, bgcolor:"#2f63ad",
              boxShadow:"none", "&:hover":{ bgcolor:"#1f4f94", boxShadow:"none" } }}>
            {savingResv ? <CircularProgress size={20} color="inherit" /> : "Confirm Reservation"}
          </Button>
        </DialogActions>
      </Dialog>

      <SetLocationDialog open={locDialog} onClose={()=>setLocDialog(false)} marina={marina}
        onSaved={(lat,lng)=>{ setMarina(m=>({...m,latitude:lat,longitude:lng})); showSnack("Marina location saved"); }} />

      {/* ── Vessel Fit Dialog ─────────────────────────────────────────────── */}
      <Dialog open={vesselFitOpen} onClose={()=>setVesselFitOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx:{ borderRadius:"18px" } }}>
        <DialogTitle sx={{ fontWeight:800, color:"#223142", display:"flex", alignItems:"center", gap:1 }}>
          <DirectionsBoatIcon sx={{ color:"#7c3aed" }} />
          Find Slips for Vessel
          <Box sx={{ flex:1 }} />
          <IconButton size="small" onClick={()=>setVesselFitOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color:"#6b778c", mb:2.5 }}>
            Select a vessel from the registry or enter dimensions manually. The map will highlight only available slips that can fit the vessel.
          </Typography>

          {/* Vessel selector */}
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>Select from Vessel Registry</Typography>
          <Autocomplete
            options={vessels}
            getOptionLabel={v => `${v.vessel_name || "Unnamed"}${v.loa ? ` - ${v.loa}ft LOA` : ""}${v.beam ? ` × ${v.beam}ft beam` : ""}`}
            value={selectedVessel}
            onChange={(_, v) => {
              setSelectedVessel(v);
              if (v) { setManualLoa(""); setManualBeam(""); setManualDraft(""); }
            }}
            renderInput={params => (
              <TextField {...params} label="Vessel" size="small" placeholder="Search by name..."
                sx={{ "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
            )}
            renderOption={(props, v) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight:700 }}>{v.vessel_name || "Unnamed"}</Typography>
                  <Typography variant="caption" sx={{ color:"#6b778c" }}>
                    {[v.loa && `LOA: ${v.loa}ft`, v.beam && `Beam: ${v.beam}ft`, v.draft && `Draft: ${v.draft}ft`].filter(Boolean).join(" · ") || "No dimensions recorded"}
                  </Typography>
                </Box>
              </Box>
            )}
            sx={{ mb:2 }}
          />

          <Divider sx={{ my:2 }}>
            <Typography variant="caption" sx={{ color:"#6b778c", fontWeight:600 }}>OR ENTER MANUALLY</Typography>
          </Divider>

          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1.5 }}>Manual Dimensions</Typography>
          <Box sx={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:1.5 }}>
            <TextField label="LOA (ft)" size="small" type="number" value={manualLoa}
              onChange={e=>{ setManualLoa(e.target.value); setSelectedVessel(null); }}
              disabled={!!selectedVessel}
              sx={{ "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
            <TextField label="Beam (ft)" size="small" type="number" value={manualBeam}
              onChange={e=>{ setManualBeam(e.target.value); setSelectedVessel(null); }}
              disabled={!!selectedVessel}
              sx={{ "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
            <TextField label="Draft (ft)" size="small" type="number" value={manualDraft}
              onChange={e=>{ setManualDraft(e.target.value); setSelectedVessel(null); }}
              disabled={!!selectedVessel}
              sx={{ "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }} />
          </Box>

          {/* Preview of what we'll filter on */}
          {(selectedVessel || manualLoa || manualBeam || manualDraft) && (
            <Box sx={{ mt:2, p:1.5, borderRadius:"10px", backgroundColor:"rgba(124,58,237,0.06)", border:"1px solid rgba(124,58,237,0.2)" }}>
              <Typography variant="caption" sx={{ fontWeight:700, color:"#7c3aed" }}>Will filter slips for:</Typography>
              <Typography variant="body2" sx={{ color:"#223142", mt:0.25 }}>
                {selectedVessel ? selectedVessel.vessel_name : "Manual entry"} -&nbsp;
                {[
                  (selectedVessel?.loa   || manualLoa)   && `LOA ≤ ${selectedVessel?.loa   || manualLoa}ft`,
                  (selectedVessel?.beam  || manualBeam)  && `Beam ≤ ${selectedVessel?.beam  || manualBeam}ft`,
                  (selectedVessel?.draft || manualDraft) && `Draft ≤ ${selectedVessel?.draft || manualDraft}ft`,
                ].filter(Boolean).join(", ") || "no dimension constraints"}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px:3, pb:3, gap:1 }}>
          {fitDims && (
            <Button onClick={clearVesselFit} variant="outlined" color="error"
              sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700 }}>
              Clear Filter
            </Button>
          )}
          <Box sx={{ flex:1 }} />
          <Button onClick={()=>setVesselFitOpen(false)} variant="outlined"
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:600 }}>Cancel</Button>
          <Button onClick={applyVesselFit} variant="contained" disabled={!selectedVessel && !manualLoa && !manualBeam && !manualDraft}
            sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, backgroundColor:"#7c3aed", "&:hover":{ backgroundColor:"#6d28d9" } }}>
            Show Fitting Slips
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}>
        <Alert severity={snack.sev} onClose={()=>setSnack(s=>({...s,open:false}))} sx={{ borderRadius:"10px" }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default MarinaMap;
